import React from 'react';
import { Box, Typography, Button, Container, useTheme, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowForward, Speed, TrendingUp, Security, VerifiedUser } from '@mui/icons-material';

const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        background: `
          radial-gradient(circle at 20% 80%, rgba(74, 144, 226, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(92, 107, 192, 0.15) 0%, transparent 50%),
          linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #121212 100%)
        `,
        minHeight: '93vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: 'linear-gradient(45deg, rgba(74, 144, 226, 0.1), rgba(92, 107, 192, 0.1))',
          borderRadius: '50%',
          filter: 'blur(40px)',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '200px',
          height: '200px',
          background: 'linear-gradient(135deg, rgba(92, 107, 192, 0.1), rgba(74, 144, 226, 0.1))',
          borderRadius: '50%',
          filter: 'blur(30px)',
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      {/* Geometric Patterns */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(74, 144, 226, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(74, 144, 226, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          opacity: 0.5,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto' }}>
          {/* Trust Badges */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 2, 
            mb: 4,
            flexWrap: 'wrap'
          }}>
            <Chip
              icon={<Speed />}
              label={t('home.trust.fast')}
              size="small"
              sx={{
                bgcolor: 'rgba(74, 144, 226, 0.1)',
                color: theme.palette.primary.main,
                border: '1px solid rgba(74, 144, 226, 0.2)',
                '& .MuiChip-icon': { color: theme.palette.primary.main }
              }}
            />
            <Chip
              icon={<TrendingUp />}
              label={t('home.trust.price')}
              size="small"
              sx={{
                bgcolor: 'rgba(92, 107, 192, 0.1)',
                color: theme.palette.secondary.main,
                border: '1px solid rgba(92, 107, 192, 0.2)',
                '& .MuiChip-icon': { color: theme.palette.secondary.main }
              }}
            />
            <Chip
              icon={<VerifiedUser />}
              label={t('home.trust.team')}
              size="small"
              sx={{
                bgcolor: 'rgba(102, 187, 106, 0.1)',
                color: '#66BB6A',
                border: '1px solid rgba(102, 187, 106, 0.2)',
                '& .MuiChip-icon': { color: '#66BB6A' }
              }}
            />
          </Box>

          {/* Main Heading */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
              mb: 3,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              background: `linear-gradient(135deg, #ffffff 0%, ${theme.palette.primary.main} 50%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              position: 'relative',
              textShadow: '0 0 40px rgba(74, 144, 226, 0.3)',
              animation: 'fadeInUp 1s ease-out',
              '@keyframes fadeInUp': {
                '0%': { opacity: 0, transform: 'translateY(30px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            {t('home.hero.headline')}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="h4"
            sx={{
              mb: 6,
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6,
              fontWeight: 400,
              maxWidth: '600px',
              mx: 'auto',
              animation: 'fadeInUp 1s ease-out 0.2s both',
            }}
          >
            {t('home.hero.subheadline')}
          </Typography>

          {/* CTA Buttons */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 3, 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 8,
            animation: 'fadeInUp 1s ease-out 0.4s both',
          }}>
            <Button
              onClick={() => {
                const formSection = document.getElementById('quick-order-form');
                if (formSection) {
                  formSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{
                fontSize: '1.1rem',
                py: 2,
                px: 5,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: `0 8px 32px rgba(74, 144, 226, 0.4)`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: '200px',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: `0 12px 40px rgba(74, 144, 226, 0.6)`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                },
                '&:active': {
                  transform: 'translateY(-2px) scale(1.01)',
                }
              }}
            >
              {t('home.hero.cta')}
            </Button>
            
            <Button
              component={Link}
              to="/services"
              variant="outlined"
              size="large"
              sx={{
                fontSize: '1.1rem',
                py: 2,
                px: 5,
                borderRadius: 3,
                borderWidth: 2,
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: '200px',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: 'rgba(74, 144, 226, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(74, 144, 226, 0.2)',
                }
              }}
            >
              {t('services.title')}
            </Button>
          </Box>

          {/* Stats Section */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 4, md: 8 },
              flexWrap: 'wrap',
              animation: 'fadeInUp 1s ease-out 0.6s both',
            }}
          >
            {[
              { number: '24h', label: t('home.stats.delivery') },
              { number: t('home.stats.proNumber'), label: t('home.stats.proLabel') },
              { number: t('home.stats.priceNumber'), label: t('home.stats.priceLabel') },
            ].map((stat, index) => (
              <Box key={index} sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 1,
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection; 